import type { FC } from 'react';
import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { parseFloatOrFallback } from '@wix/da-shared-react/pkg/EarningsCalculator';
import PayPalWithdrawSetupModal from '../PayPalWithdrawSetupModal';
import PayPalWithdrawAmountModal from '../PayPalWithdrawAmountModal';
import PayPalWithdrawConfirmationModal from '../PayPalWithdrawConfirmationModal';
import {
  getAvailableBalance,
  getPaypalEmail,
  getPaypalIsMissingCountry,
  getPaypalMinimumWithdrawalAmount,
} from '../../../../../selectors/earnings';

export interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type ModalStep = 'setup' | 'amount' | 'confirmation';

const PayPalWithdrawRootModal: FC<Props> = ({ isOpen, onClose }) => {
  const availableBalance = useSelector(getAvailableBalance);
  const paypalEmail = useSelector(getPaypalEmail);
  const paypalIsMissingCountry = useSelector(getPaypalIsMissingCountry);
  const paypalMinimumWithdrawalAmount = useSelector(
    getPaypalMinimumWithdrawalAmount
  );

  // Determine initial step based on PayPal setup status
  const getInitialStep = useCallback((): ModalStep => {
    if (!paypalEmail || paypalIsMissingCountry) {
      return 'setup';
    }
    return 'amount';
  }, [paypalEmail, paypalIsMissingCountry]);

  const [currentStep, setCurrentStep] = useState<ModalStep>('setup');

  // Reset to initial step when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(getInitialStep());
    }
  }, [isOpen, getInitialStep]);

  const handleSetupContinue = useCallback(() => {
    setCurrentStep('amount');
  }, []);

  const handleAmountContinue = useCallback(() => {
    setCurrentStep('confirmation');
  }, []);

  const handleConfirmationDone = useCallback(() => {
    onClose();
    window.location.reload();
  }, [onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleCloseAndReload = useCallback(() => {
    handleClose();
    window.location.reload();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <PayPalWithdrawSetupModal
        isOpen={currentStep === 'setup'}
        requestClose={handleClose}
        onSuccess={handleSetupContinue}
        paypalEmail={paypalEmail}
        isMissingCountry={paypalIsMissingCountry}
      />
      <PayPalWithdrawAmountModal
        isOpen={currentStep === 'amount'}
        requestClose={handleClose}
        availableBalance={parseFloatOrFallback(availableBalance, 0)}
        paypalEmail={paypalEmail}
        minimumWithdrawalAmount={parseFloatOrFallback(
          paypalMinimumWithdrawalAmount,
          0
        )}
        onContinue={handleAmountContinue}
      />
      <PayPalWithdrawConfirmationModal
        isOpen={currentStep === 'confirmation'}
        requestClose={handleCloseAndReload}
        paypalEmail={paypalEmail}
        onDone={handleConfirmationDone}
      />
    </>
  );
};

PayPalWithdrawRootModal.displayName = 'PayPalWithdrawRootModal';

export default PayPalWithdrawRootModal;
